/**
 * WetSignScanner — v3 in-branch wet-sign completion via QR-marker scan.
 *
 * Wave 4 Agent D port (2026-05-09) of the SPFx WetSignScanner. Reuses the
 * /api/signing/wet-complete Function endpoint that was already extended for
 * v3 (SignNowFunction.cs ~line 1654). The QR payload format is unchanged
 * from SPFx — server accepts compact + long key forms.
 *
 * UX changes vs SPFx
 * ------------------
 * - Full-screen scanner viewport with a visible scan-target overlay.
 * - Header status banner ("Point camera at the QR code on each signed page").
 * - Camera-device picker (front/back/external) sourced from
 *   `BrowserCodeReader.listVideoInputDevices()` so the FSR can switch to the
 *   rear camera on iPhone for sharper QR capture.
 * - Per-page scan tracking — duplicates of the same docInstanceId+page are
 *   deduplicated client-side BEFORE POST so the server doesn't see the same
 *   wet-complete twice.
 * - Optimistic UI: page chip flips green immediately on decode; reverts +
 *   surfaces an error toast if the POST fails or the server returns 422.
 * - "Done" button enabled when the count of scanned pages equals expectedPages
 *   (or stays "Continue Scanning" when expectedPages is unknown).
 *
 * Library
 * -------
 * - @zxing/browser (MIT) — same as SPFx; bundled via npm.
 * - NO CDN (PCB corporate net blocks externals — global rule).
 *
 * Reachability
 * ------------
 * The scanner is reusable from anywhere with an active cart + signing-session
 * id. Today's expected mount point is the SigningPanel right pane in the cart
 * pipeline (Sub-phase 1d), but exposing it as a self-contained component lets
 * other surfaces (e.g. CIF Maintenance "redo wet-sign") reuse it later.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  BrowserMultiFormatReader,
  type IScannerControls,
} from '@zxing/browser';
import type { Result } from '@zxing/library';
import { useApiClient } from '@/contexts/ApiClientContext';
import { asIso8601, type WetSignAttestation } from '@retail-ops/shared-types';

// ── Props + result types ───────────────────────────────────────────────────

export interface WetSignScannerProps {
  /** Active cart id — must match the QR payload's `c` field. */
  cartId: string;
  /** Active signing-session id — also used as the SigningGroupId server-side. */
  sessionId: string;
  /** Officer short code — surfaced in the SessionEvent Actor field. */
  officerCode?: string;
  /**
   * Optional total page count expected for the current package. When
   * provided, the "Done" button enables once `scannedPages.size >= expectedPages`.
   * When omitted, the FSR clicks "Done" manually.
   */
  expectedPages?: number;
  /** Optional default signer email (when QR doesn't carry one). */
  defaultSignerEmail?: string;
  /** Optional default signer role (when QR doesn't carry one). */
  defaultSignerRole?: string;
  /** Optional default signer display name — surfaced in the attestation step. */
  defaultSignerName?: string;
  /** Called once the FSR clicks Done — usually triggers a session refresh. */
  onComplete: (summary: WetSignScanSummary) => void;
  /** Called when the FSR closes the scanner without completing. */
  onCancel: () => void;
}

export interface WetSignScanSummary {
  cartId: string;
  sessionId: string;
  pagesSigned: number;
  totalPages?: number;
  scans: WetSignScanRecord[];
}

interface WetSignScanRecord {
  docInstanceId: string;
  page: number;
  totalPages?: number;
  templateCode?: string;
  recipientRole?: string;
  scannedAt: string;
  serverEventId?: string;
}

interface ParsedQrPayload {
  cartId?: string;
  docInstanceId?: string;
  templateCode?: string;
  page?: number;
  totalPages?: number;
  renderVersion?: number;
  recipientRole?: string;
  officerCode?: string;
  raw: string;
}

// ── Styles (PCB brand — primary #2968ad, dark #1a406a, green #386732, red #b10505) ──

const overlayS: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.92)',
  zIndex: 9000,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
  color: 'var(--topbar-fg)',
};

const topBarS: CSSProperties = {
  background: 'var(--topbar-solid)',
  padding: '12px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flex: '0 0 auto',
};

const statusBannerS: CSSProperties = {
  background: 'var(--brand-fill)',
  color: 'var(--on-brand)',
  padding: '10px 20px',
  textAlign: 'center',
  fontSize: 14,
  fontWeight: 500,
  flex: '0 0 auto',
};

const stageS: CSSProperties = {
  flex: '1 1 auto',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#000', // theme-exempt: camera viewport stays black
  overflow: 'hidden',
};

const videoS: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const targetOverlayS: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(60vw, 360px)',
  height: 'min(60vw, 360px)',
  border: '3px solid rgba(255, 255, 255, 0.85)',
  borderRadius: 12,
  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
  pointerEvents: 'none',
};

const targetOverlayHitS: CSSProperties = {
  ...targetOverlayS,
  border: '3px solid var(--ok-fill)',
  background: 'rgba(56, 103, 50, 0.15)',
};

const bottomBarS: CSSProperties = {
  background: 'var(--topbar-solid)',
  padding: '12px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  flex: '0 0 auto',
};

const chipsRowS: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  maxHeight: 80,
  overflowY: 'auto',
};

const chipS = (status: 'pending' | 'sent' | 'error'): CSSProperties => ({
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 500,
  background:
    status === 'sent' ? 'var(--ok-fill)' :
    status === 'error' ? 'var(--err-fill)' :
    'var(--brand-fill)',
  color: 'var(--on-brand)',
});

const buttonRowS: CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  justifyContent: 'space-between',
};

const buttonS: CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: '1px solid var(--pcb-blue)',
  background: 'var(--brand-fill)',
  color: 'var(--on-brand)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const buttonGhostS: CSSProperties = {
  ...buttonS,
  background: 'transparent',
  border: '1px solid var(--topbar-ctl-border)',
  color: 'var(--topbar-fg)',
};

const buttonDoneS: CSSProperties = {
  ...buttonS,
  background: 'var(--ok-fill)',
  border: '1px solid var(--ok-fill)',
};

const buttonDoneDisabledS: CSSProperties = {
  ...buttonDoneS,
  background: 'var(--ink-500)', // disabled
  border: '1px solid var(--input-border)',
  cursor: 'not-allowed',
};

const selectS: CSSProperties = {
  background: 'rgba(255,255,255,0.12)',
  color: 'var(--topbar-fg)',
  border: '1px solid rgba(255,255,255,0.25)',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: 12,
};

// ── Attestation overlay styles (Q8 — Bill 2026-05-10) ──────────────────────

const attestPanelS: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.97)',
  zIndex: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  overflowY: 'auto',
};

const attestCardS: CSSProperties = {
  background: 'var(--surface-card)',
  color: 'var(--ink-900)',
  borderRadius: 10,
  maxWidth: 640,
  width: '100%',
  padding: 24,
  boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const attestHeadingS: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: 'var(--pcb-blue-dark)',
};

const attestIntroS: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--ink-800)',
  margin: 0,
};

const attestRowS: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  background: 'var(--surface-subtle)',
  border: '1px solid var(--surface-border)',
  borderRadius: 6,
};

const attestCheckS: CSSProperties = {
  marginTop: 2,
  width: 18,
  height: 18,
  flex: '0 0 auto',
  accentColor: 'var(--ok-fill)',
};

const attestLabelS: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--ink-900)',
  lineHeight: 1.4,
  flex: 1,
};

const attestSubFieldS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginTop: 10,
  paddingLeft: 28,
};

const attestInputS: CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--surface-border)',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
};

const attestSelectS: CSSProperties = {
  ...attestInputS,
  background: 'var(--surface-card)',
};

const attestSubLabelS: CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-700)',
  fontWeight: 500,
};

const attestPrimaryBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid var(--ok-fill)',
  background: 'var(--ok-fill)',
  color: 'var(--on-brand)',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const attestDisabledBtnS: CSSProperties = {
  ...attestPrimaryBtnS,
  background: 'var(--ink-500)', // disabled
  border: '1px solid var(--input-border)',
  cursor: 'not-allowed',
};

const attestGhostBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid var(--surface-border)',
  background: 'var(--surface-card)',
  color: 'var(--ink-900)',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const attestFooterS: CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 8,
};

const attestErrorS: CSSProperties = {
  background: 'var(--err-bg)',
  color: 'var(--err-text)',
  border: '1px solid var(--err-line)',
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 13,
};

const toastS = (kind: 'error' | 'info'): CSSProperties => ({
  position: 'absolute',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  background: kind === 'error' ? 'var(--err-fill)' : 'var(--brand-fill)',
  color: 'var(--on-brand)',
  padding: '8px 14px',
  borderRadius: 6,
  fontSize: 13,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  zIndex: 10,
});

// ── Helpers ────────────────────────────────────────────────────────────────

function parseQrText(text: string): ParsedQrPayload {
  // QrMarkerService emits compact keys (c/d/t/pn/tp/v/r/o); the server-side
  // /signing/wet-complete endpoint accepts both compact + long form. Mirror
  // SPFx parser (WetSignScanner.tsx parseQrText) for symmetry.
  const out: ParsedQrPayload = { raw: text };
  try {
    const obj = JSON.parse(text) as Record<string, unknown>;
    out.cartId = (obj.c as string) ?? (obj.cartId as string) ?? undefined;
    out.docInstanceId = (obj.d as string) ?? (obj.docInstanceId as string) ?? undefined;
    out.templateCode = (obj.t as string) ?? (obj.templateCode as string) ?? undefined;
    out.recipientRole = (obj.r as string) ?? (obj.recipientRole as string) ?? undefined;
    out.officerCode = (obj.o as string) ?? (obj.officerCode as string) ?? undefined;

    const pn = (obj.pn as number | string | undefined) ?? (obj.page as number | string | undefined);
    if (typeof pn === 'number') out.page = pn;
    else if (typeof pn === 'string') {
      const n = parseInt(pn, 10);
      if (!Number.isNaN(n)) out.page = n;
    }

    const tp = (obj.tp as number | string | undefined) ?? (obj.totalPages as number | string | undefined);
    if (typeof tp === 'number') out.totalPages = tp;
    else if (typeof tp === 'string') {
      const n = parseInt(tp, 10);
      if (!Number.isNaN(n)) out.totalPages = n;
    }

    const rv = (obj.v as number | string | undefined) ?? (obj.renderVersion as number | string | undefined);
    if (typeof rv === 'number') out.renderVersion = rv;
    else if (typeof rv === 'string') {
      const n = parseInt(rv, 10);
      if (!Number.isNaN(n)) out.renderVersion = n;
    }
  } catch {
    // Keep raw — server returns 422 if it can't parse either.
  }
  return out;
}

function dedupeKey(p: ParsedQrPayload): string {
  // Same doc + same page = same scan (regardless of how many times the FSR
  // re-aims at it). Falls back to raw payload hash when fields are missing.
  if (p.docInstanceId && p.page !== undefined) {
    return `${p.docInstanceId}|p${p.page}`;
  }
  if (p.docInstanceId) return p.docInstanceId;
  return p.raw;
}

// ── Component ──────────────────────────────────────────────────────────────

type ScannerState =
  | 'idle'
  | 'starting'
  | 'scanning'
  | 'submitting'
  | 'done'
  | 'no-camera';

// ── Attestation step state ─────────────────────────────────────────────────

type IdType = 'DL' | 'Passport' | 'StateID' | 'Other';

interface AttestationFormState {
  idType: IdType;
  idLast4: string;
  witnessedSignature: boolean;
  originalDocument: boolean;
  dateMatches: boolean;
  dateMismatchExplanation: string;
}

function emptyAttestationForm(): AttestationFormState {
  return {
    idType: 'DL',
    idLast4: '',
    witnessedSignature: false,
    originalDocument: false,
    dateMatches: false,
    dateMismatchExplanation: '',
  };
}

function attestationIsValid(f: AttestationFormState): boolean {
  if (!f.witnessedSignature || !f.originalDocument) return false;
  if (!/^\d{4}$/.test(f.idLast4)) return false;
  if (!f.dateMatches && f.dateMismatchExplanation.trim().length < 5) return false;
  return true;
}

export function WetSignScanner(props: WetSignScannerProps): JSX.Element {
  const {
    cartId,
    sessionId,
    officerCode,
    expectedPages,
    defaultSignerEmail,
    defaultSignerRole,
    defaultSignerName,
    onComplete,
    onCancel,
  } = props;

  const api = useApiClient();

  const [state, setState] = useState<ScannerState>('idle');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>();
  const [scans, setScans] = useState<WetSignScanRecord[]>([]);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  const [errorKeys, setErrorKeys] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);
  const [hitFlash, setHitFlash] = useState<boolean>(false);

  // Q8 (Bill 2026-05-10) — FSR attestation gate before final upload.
  const [showAttestation, setShowAttestation] = useState<boolean>(false);
  const [attestForm, setAttestForm] = useState<AttestationFormState>(() => emptyAttestationForm());
  const [attestSubmitting, setAttestSubmitting] = useState<boolean>(false);
  const [attestError, setAttestError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const isMountedRef = useRef<boolean>(true);
  // Mirror the scanned-keys set in a ref so the decode callback (created
  // inside an effect) always sees the latest dedup set without needing to
  // re-bind on every state change. State stays the source of truth for renders.
  const scannedKeysRef = useRef<Set<string>>(new Set());

  // ── Cleanup on unmount ────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopReader();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Device enumeration ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // listVideoInputDevices triggers a getUserMedia probe to populate
        // device labels — without it, devices return empty `label`.
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        if (cancelled) return;
        setDevices(list);
        if (list.length === 0) {
          setState('no-camera');
          return;
        }
        // Prefer rear-facing cam (label often contains "back" or "environment").
        const rear = list.find((d) =>
          /back|rear|environment/i.test(d.label || '')
        );
        setSelectedDeviceId((rear ?? list[0]).deviceId);
      } catch (err) {
        if (cancelled) return;
        // Most common case: user denied camera permission OR the host has no
        // camera (desktop without webcam). Surface clearly + keep the scanner
        // mounted so the FSR can still cancel gracefully.
        // eslint-disable-next-line no-console
        console.warn('[WetSignScanner] enumerateDevices failed', err);
        setState('no-camera');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Start/stop reader on device-id change ─────────────────────────────
  useEffect(() => {
    if (!selectedDeviceId) return;
    if (state === 'no-camera') return;

    void startReader(selectedDeviceId);
    return () => {
      stopReader();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId]);

  function stopReader(): void {
    if (controlsRef.current) {
      try {
        controlsRef.current.stop();
      } catch {
        /* ignore */
      }
      controlsRef.current = null;
    }
  }

  async function startReader(deviceId: string): Promise<void> {
    setState('starting');
    stopReader();

    if (!videoRef.current) {
      setState('idle');
      return;
    }

    try {
      const reader = readerRef.current ?? new BrowserMultiFormatReader();
      readerRef.current = reader;

      const controls = await reader.decodeFromVideoDevice(
        deviceId,
        videoRef.current,
        (result: Result | undefined, _err: unknown, _ctrl: IScannerControls) => {
          if (!isMountedRef.current) return;
          if (!result) return;
          // err is typically NotFoundException per frame — ignore.
          const text = result.getText();
          handleDecode(text);
        }
      );
      controlsRef.current = controls;
      setState('scanning');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[WetSignScanner] decodeFromVideoDevice failed', err);
      setState('no-camera');
    }
  }

  // ── Decode handler ────────────────────────────────────────────────────

  const handleDecode = useCallback(
    (text: string) => {
      const parsed = parseQrText(text);

      if (parsed.cartId && parsed.cartId !== cartId) {
        showToast(
          'error',
          'QR is from a different cart — re-print or scan a page from this cart.'
        );
        return;
      }

      const key = dedupeKey(parsed);
      if (scannedKeysRef.current.has(key)) {
        // Silent no-op: already scanned. The chip is already green.
        return;
      }
      scannedKeysRef.current.add(key);

      // Optimistic insert as "pending" — flips to "sent" on POST success.
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      const record: WetSignScanRecord = {
        docInstanceId: parsed.docInstanceId ?? '(unknown)',
        page: parsed.page ?? 0,
        totalPages: parsed.totalPages,
        templateCode: parsed.templateCode,
        recipientRole: parsed.recipientRole ?? defaultSignerRole,
        scannedAt: new Date().toISOString(),
      };
      setScans((prev) => [...prev, record]);

      flashHit();
      void postWetComplete(parsed, record, key);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartId, defaultSignerRole]
  );

  function flashHit(): void {
    setHitFlash(true);
    window.setTimeout(() => {
      if (isMountedRef.current) setHitFlash(false);
    }, 350);
  }

  function showToast(kind: 'error' | 'info', text: string): void {
    setToast({ kind, text });
    window.setTimeout(() => {
      if (isMountedRef.current) setToast(null);
    }, 4000);
  }

  // ── Server submission ─────────────────────────────────────────────────

  async function postWetComplete(
    parsed: ParsedQrPayload,
    record: WetSignScanRecord,
    key: string
  ): Promise<void> {
    try {
      const result = await api.signing.completeWetSign({
        CartId: cartId,
        SessionId: sessionId,
        QrPayload: parsed.raw,
        SignedAtUtc: asIso8601(new Date().toISOString()),
        SignerEmail: defaultSignerEmail ?? '',
        SignerRole: defaultSignerRole ?? parsed.recipientRole ?? '',
        OfficerCode: officerCode ?? parsed.officerCode ?? '',
      });

      if (!isMountedRef.current) return;

      // Move from pending → sent.
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setScans((prev) =>
        prev.map((s) =>
          s.docInstanceId === record.docInstanceId && s.page === record.page
            ? { ...s, serverEventId: result.EventId ?? undefined }
            : s
        )
      );
      showToast('info', `Page ${record.page || '?'} marked signed`);
    } catch (err) {
      if (!isMountedRef.current) return;
      // Revert dedup so the FSR can re-scan after fixing whatever the server
      // flagged (cart mismatch, stale render, etc.). Move into the error set
      // so the chip turns red.
      scannedKeysRef.current.delete(key);
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setErrorKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      const msg = err instanceof Error ? err.message : 'Server rejected scan';
      showToast('error', msg);
    }
  }

  // ── Derived counts ────────────────────────────────────────────────────

  const successCount = useMemo(
    () => scans.filter((s) => s.serverEventId !== undefined).length,
    [scans]
  );
  const allDone =
    expectedPages !== undefined && successCount >= expectedPages;

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div role="dialog" aria-label="Wet-sign scanner" style={overlayS}>
      <div style={topBarS}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Wet-Sign Scanner</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {devices.length > 1 && (
            <select
              aria-label="Camera"
              style={selectS}
              value={selectedDeviceId ?? ''}
              onChange={(e) => setSelectedDeviceId(e.target.value || undefined)}
            >
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `Camera ${d.deviceId.substring(0, 6)}`}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            style={buttonGhostS}
            onClick={() => {
              stopReader();
              onCancel();
            }}
          >
            Close
          </button>
        </div>
      </div>

      <div style={statusBannerS}>
        {state === 'no-camera'
          ? 'No camera available — close and use a device with a camera.'
          : 'Point camera at the QR code on each signed page'}
      </div>

      <div style={stageS}>
        <video ref={videoRef} style={videoS} autoPlay muted playsInline />
        <div style={hitFlash ? targetOverlayHitS : targetOverlayS} />
        {toast && <div style={toastS(toast.kind)}>{toast.text}</div>}
      </div>

      <div style={bottomBarS}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {expectedPages !== undefined
              ? `Pages signed: ${successCount} of ${expectedPages}`
              : `Pages signed: ${successCount}`}
          </div>
          {pendingKeys.size > 0 && (
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              ({pendingKeys.size} sending…)
            </div>
          )}
        </div>

        <div style={chipsRowS}>
          {scans.map((s) => {
            const k = `${s.docInstanceId}|p${s.page}`;
            const status = errorKeys.has(k)
              ? 'error'
              : pendingKeys.has(k)
                ? 'pending'
                : 'sent';
            return (
              <div key={k} style={chipS(status)}>
                {s.templateCode ? `${s.templateCode} · ` : ''}page {s.page || '?'}
              </div>
            );
          })}
          {scans.length === 0 && (
            <div style={{ fontSize: 12, opacity: 0.6 }}>
              No pages scanned yet.
            </div>
          )}
        </div>

        <div style={buttonRowS}>
          <button
            type="button"
            style={buttonGhostS}
            onClick={() => {
              stopReader();
              onCancel();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            style={
              expectedPages !== undefined && !allDone
                ? buttonDoneDisabledS
                : buttonDoneS
            }
            disabled={expectedPages !== undefined && !allDone}
            onClick={() => {
              if (state === 'submitting') return;
              // Q8 (Bill 2026-05-10) — pause the reader and surface the FSR
              // attestation step BEFORE the upload completes. The scan
              // metadata is already mirrored to the server via per-page
              // wet-complete calls; the attestation submit is the final
              // audit-anchor that closes out the wet-sign session.
              stopReader();
              setAttestError(null);
              setShowAttestation(true);
            }}
          >
            {expectedPages !== undefined && !allDone
              ? 'Continue Scanning'
              : 'Done'}
          </button>
        </div>
      </div>

      {showAttestation && (
        <div role="dialog" aria-label="Wet-sign attestation" style={attestPanelS}>
          <div style={attestCardS}>
            <h2 style={attestHeadingS}>Before we upload — please confirm</h2>
            <p style={attestIntroS}>
              Wet-signed records become a permanent part of the customer&apos;s
              file. Your confirmation here is itself a regulatory record under
              federal e-sign law, so please answer truthfully — if any of the
              four can&apos;t be checked, choose <strong>Cancel</strong> and let
              your manager know.
            </p>

            {/* 1. Photo ID verified */}
            <div style={attestRowS}>
              <input
                type="checkbox"
                id="att-photo-id"
                style={attestCheckS}
                checked={
                  // The photo-ID check is implicit: it's "verified" once the
                  // ID type is picked and last 4 are typed correctly. We
                  // surface it as a sub-field rather than a checkbox so the
                  // FSR can't tick the box without entering the evidence.
                  /^\d{4}$/.test(attestForm.idLast4)
                }
                readOnly
                aria-readonly
                aria-label="Photo ID verified (auto-checks when ID type + last 4 are entered below)"
              />
              <div style={{ flex: 1 }}>
                <label htmlFor="att-id-type" style={attestLabelS}>
                  I verified the signer&apos;s photo ID.
                </label>
                <div style={attestSubFieldS}>
                  <span style={attestSubLabelS}>ID type</span>
                  <select
                    id="att-id-type"
                    style={attestSelectS}
                    value={attestForm.idType}
                    onChange={(e) =>
                      setAttestForm((f) => ({ ...f, idType: e.target.value as IdType }))
                    }
                  >
                    <option value="DL">Driver&apos;s License</option>
                    <option value="Passport">Passport</option>
                    <option value="StateID">State ID</option>
                    <option value="Other">Other government ID</option>
                  </select>
                  <span style={attestSubLabelS}>Last 4 digits of the ID number</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    pattern="\d{4}"
                    style={attestInputS}
                    placeholder="e.g. 4821"
                    value={attestForm.idLast4}
                    onChange={(e) =>
                      setAttestForm((f) => ({
                        ...f,
                        idLast4: e.target.value.replace(/\D/g, '').slice(0, 4),
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            {/* 2. Original document */}
            <div style={attestRowS}>
              <input
                type="checkbox"
                id="att-original"
                style={attestCheckS}
                checked={attestForm.originalDocument}
                onChange={(e) =>
                  setAttestForm((f) => ({ ...f, originalDocument: e.target.checked }))
                }
              />
              <label htmlFor="att-original" style={attestLabelS}>
                This is the signed original — not a photocopy, fax, or
                scan-of-a-scan.
              </label>
            </div>

            {/* 3. Witnessed the signature */}
            <div style={attestRowS}>
              <input
                type="checkbox"
                id="att-witnessed"
                style={attestCheckS}
                checked={attestForm.witnessedSignature}
                onChange={(e) =>
                  setAttestForm((f) => ({ ...f, witnessedSignature: e.target.checked }))
                }
              />
              <label htmlFor="att-witnessed" style={attestLabelS}>
                I witnessed the signature being placed — or the signer affirmed
                in my presence that the signature is theirs.
              </label>
            </div>

            {/* 4. Date matches */}
            <div style={attestRowS}>
              <input
                type="checkbox"
                id="att-date"
                style={attestCheckS}
                checked={attestForm.dateMatches}
                onChange={(e) =>
                  setAttestForm((f) => ({
                    ...f,
                    dateMatches: e.target.checked,
                    dateMismatchExplanation: e.target.checked ? '' : f.dateMismatchExplanation,
                  }))
                }
              />
              <div style={{ flex: 1 }}>
                <label htmlFor="att-date" style={attestLabelS}>
                  The date on the signature matches today&apos;s date.
                </label>
                {!attestForm.dateMatches && (
                  <div style={attestSubFieldS}>
                    <span style={attestSubLabelS}>
                      Explain the date difference — e.g. &quot;customer signed
                      at home on 2026-05-08, brought it in today&quot;
                    </span>
                    <textarea
                      style={{ ...attestInputS, minHeight: 60, fontFamily: 'inherit' }}
                      value={attestForm.dateMismatchExplanation}
                      onChange={(e) =>
                        setAttestForm((f) => ({
                          ...f,
                          dateMismatchExplanation: e.target.value,
                        }))
                      }
                      placeholder="Tell us why the signature date differs from today"
                    />
                  </div>
                )}
              </div>
            </div>

            {attestError && <div role="alert" style={attestErrorS}>{attestError}</div>}

            <div style={attestFooterS}>
              <button
                type="button"
                style={attestGhostBtnS}
                disabled={attestSubmitting}
                onClick={() => {
                  setShowAttestation(false);
                  // Don't fire onCancel — the FSR may want to re-scan a page.
                  // We just dismiss the attestation panel.
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                style={
                  attestationIsValid(attestForm) && !attestSubmitting
                    ? attestPrimaryBtnS
                    : attestDisabledBtnS
                }
                disabled={!attestationIsValid(attestForm) || attestSubmitting}
                onClick={() => {
                  void submitAttestation();
                }}
              >
                {attestSubmitting ? 'Submitting…' : 'Confirm and upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Final attestation submit ──────────────────────────────────────────
  async function submitAttestation(): Promise<void> {
    if (!attestationIsValid(attestForm)) return;
    setAttestSubmitting(true);
    setAttestError(null);
    try {
      const payload: WetSignAttestation = {
        IdTypeVerified: attestForm.idType,
        IdLast4: attestForm.idLast4,
        WitnessedSignature: attestForm.witnessedSignature,
        OriginalDocument: attestForm.originalDocument,
        DateMatches: attestForm.dateMatches,
        ...(attestForm.dateMatches
          ? {}
          : { DateMismatchExplanation: attestForm.dateMismatchExplanation.trim() }),
        AttestingOfficerCode: officerCode ?? '',
        AttestationTimestamp: asIso8601(new Date().toISOString()),
      };
      const resp = await api.signing.submitWetSignAttestation({
        CartId: cartId,
        SessionId: sessionId,
        ...(defaultSignerEmail ? { SignerEmail: defaultSignerEmail } : {}),
        ...(defaultSignerRole ? { SignerRole: defaultSignerRole } : {}),
        ...(defaultSignerName ? { SignerDisplayName: defaultSignerName } : {}),
        Attestation: payload,
      });
      if (!resp.Success) {
        setAttestError(resp.Error ?? 'The attestation could not be saved.');
        return;
      }
      if (!isMountedRef.current) return;
      setShowAttestation(false);
      setState('done');
      onComplete({
        cartId,
        sessionId,
        pagesSigned: successCount,
        totalPages: expectedPages,
        scans,
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      const msg = err instanceof Error ? err.message : 'Unexpected error.';
      setAttestError(msg);
    } finally {
      if (isMountedRef.current) setAttestSubmitting(false);
    }
  }
}

export default WetSignScanner;
